import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { Toaster, TooltipProvider } from '@/components/cargofl-ui';
import { OfflineOverlay } from '@/components/offline-overlay';
import { ServerDownOverlay } from '@/components/server-down-overlay';
import { ApiError } from '@/lib/api';
import { SESSION_QUERY_KEY, SessionProvider } from '@/lib/session';
import { ThemeProvider } from '@/lib/theme';
import '@/styles/globals.css';

/**
 * A 401 from any call means the session expired or was revoked mid-visit. Re-asking /auth/me
 * turns that into "unauthenticated", and RequireAuth sends them to sign-in — instead of each
 * page showing its own error until the next window focus happens to refetch the session.
 * The session query itself is skipped, or its own 401 would re-trigger it forever.
 */
function onUnauthenticated(error: Error, queryKey?: readonly unknown[]): void {
  if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
  if (queryKey?.[0] === SESSION_QUERY_KEY[0] && queryKey[1] === SESSION_QUERY_KEY[1]) return;
  void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => onUnauthenticated(error, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (error) => onUnauthenticated(error) }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retrying a 401/403/404 just delays showing the user the real answer.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root not found in index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* Inside QueryClientProvider: the session is itself a query, so the cache
            must exist before it runs. */}
        <SessionProvider>
          {/* One provider for the whole app — Radix Tooltip throws without one, and
              the sidebar's local provider only covers the sidebar. */}
          <TooltipProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </TooltipProvider>
          <Toaster position="bottom-right" />
        </SessionProvider>
        <ServerDownOverlay />
        <OfflineOverlay />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Only serves offline.html when a page load fails; see public/sw.js.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.register('/sw.js');
}
